import { Card } from '../ui/Card';
import { formatCurrency, getWalletSummary } from '../../utils';
import type { Participant } from '../../types';

interface BalanceDisplayProps {
  participant: Participant;
}

/**
 * Balance Display Component
 * Shows wallet balance with initial stake and winnings breakdown
 */
export function BalanceDisplay({ participant }: BalanceDisplayProps) {
  const summary = getWalletSummary(participant);

  return (
    <Card padding="md">
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-primary-text">Your Balance</h2>
          <div className="text-right">
            <div className="text-2xl font-bold text-primary-text">
              {formatCurrency(summary.totalAvailable)}
            </div>
            <div className="text-xs text-secondary-text">Total Available</div>
          </div>
        </div>

        {/* Current Bets Section */}
        {summary.hasPendingBets && (
          <div className="pt-3 border-t border-border">
            <h3 className="text-sm font-semibold text-primary-text mb-2">Current Bets</h3>
            <div className="grid grid-cols-3 gap-2">
              <div className="bg-tertiary-bg p-2 rounded">
                <div className="text-xs text-secondary-text mb-1">At Risk</div>
                <div className="text-sm font-bold text-primary-text">
                  {formatCurrency(summary.currentBetsAmount)}
                </div>
              </div>
              <div className="bg-tertiary-bg p-2 rounded">
                <div className="text-xs text-secondary-text mb-1">Overall Odds</div>
                <div className="text-sm font-bold text-primary-text">
                  {summary.overallOdds > 0 ? '+' : ''}{summary.overallOdds}
                </div>
              </div>
              <div className="bg-tertiary-bg p-2 rounded">
                <div className="text-xs text-secondary-text mb-1">Potential Win</div>
                <div className="text-sm font-bold text-success-green">
                  {formatCurrency(summary.potentialWinnings)}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Bets History Section */}
        <div className="pt-3 border-t border-border">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-sm text-secondary-text mb-1">Bets Lost</div>
              <div className="text-base font-medium text-error">
                {formatCurrency(summary.totalLost)}
              </div>
            </div>
            <div>
              <div className="text-sm text-secondary-text mb-1">Bets Won</div>
              <div className="text-base font-medium text-success-green">
                {formatCurrency(summary.totalWon)}
              </div>
            </div>
          </div>
        </div>

        {/* Initial Stake and Winnings Section */}
        <div className="grid grid-cols-2 gap-4 pt-3 border-t border-border">
          <div>
            <div className="text-sm text-secondary-text mb-1">Initial Stake</div>
            <div className="text-base font-medium text-primary-text">
              {formatCurrency(summary.initialStakeRemaining)} / {formatCurrency(summary.initialStake)}
            </div>
            <div className="text-xs text-muted-text mt-1">Remaining</div>
          </div>

          <div>
            <div className="text-sm text-secondary-text mb-1">Winnings Balance</div>
            <div className="text-base font-medium text-success-green">
              {formatCurrency(summary.winningsBalance)}
            </div>
            <div className="text-xs text-muted-text mt-1">Leaderboard Value</div>
          </div>
        </div>

        {summary.hasInitialStakeRemaining && (
          <div className="pt-3 border-t border-border">
            <div className="text-xs text-warning bg-warning/10 px-3 py-2 rounded">
              ⚠️ Only winnings count towards the contest. Bet All ${formatCurrency(summary.initialStake)}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}

